;(() => {
  if (window.featuredProductController) {
    window.featuredProductController.initialize()
    return
  }

class FeaturedProductMediaAutoplay {
  constructor(productInfo) {
    this.productInfo = productInfo
    this.selectedOptionNames = new Set()
    this.initializeRequiredVariantSelection()
    this.observer = new MutationObserver(() => {
      this.enforceRequiredVariantSelection()
      this.playDeferredVideos()
    })
    this.observer.observe(productInfo, { childList: true, subtree: true })
    this.playDeferredVideos()
  }

  initializeRequiredVariantSelection() {
    if (!this.productInfo.hasAttribute('data-require-variant-selection')) return

    this.instructionId = `FeaturedProductVariantInstruction-${this.productInfo.dataset.section}`
    this.productInfo.querySelectorAll('variant-selects input[name], variant-selects select[name]').forEach((control) => {
      control.setAttribute('aria-describedby', this.instructionId)
    })

    this.optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
      if (!this.productInfo.contains(data.event.target)) return
      const optionName = data.target.name
      if (optionName) this.selectedOptionNames.add(optionName)
    })

    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data.sectionId !== this.productInfo.dataset.section || !data.variant) return
      const optionCount = new Set(
        Array.from(this.productInfo.querySelectorAll('variant-selects input[name], variant-selects select[name]')).map(
          ({ name }) => name,
        ),
      ).size
      if (this.selectedOptionNames.size < optionCount) return

      this.productInfo.removeAttribute('data-require-variant-selection')
      this.productInfo.querySelector('.shopify-payment-button')?.removeAttribute('hidden')
      this.productInfo.querySelectorAll(`[aria-describedby="${this.instructionId}"]`).forEach((control) => {
        control.removeAttribute('aria-describedby')
      })
      this.optionChangeUnsubscriber?.()
      this.variantChangeUnsubscriber?.()
    })

    this.enforceRequiredVariantSelection()
  }

  enforceRequiredVariantSelection() {
    if (!this.productInfo.hasAttribute('data-require-variant-selection')) return

    this.productInfo.querySelectorAll('variant-selects input[name], variant-selects select[name]').forEach((control) => {
      control.setAttribute('aria-describedby', this.instructionId)
    })

    this.productInfo.querySelectorAll('variant-selects input[name]').forEach((input) => {
      if (!this.selectedOptionNames.has(input.name)) input.checked = false
    })

    this.productInfo.querySelectorAll('variant-selects select[name]').forEach((select) => {
      if (this.selectedOptionNames.has(select.name)) return
      let prompt = select.querySelector('option[data-variant-prompt]')
      if (!prompt) {
        prompt = document.createElement('option')
        prompt.dataset.variantPrompt = 'true'
        prompt.value = ''
        const optionName = select.name.replace(/^options\[|\]$/g, '')
        prompt.textContent = (this.productInfo.dataset.chooseOption || 'Choose __OPTION__').replace('__OPTION__', optionName)
        prompt.disabled = true
        select.prepend(prompt)
      }
      select.querySelectorAll('option[selected]').forEach((option) => option.removeAttribute('selected'))
      prompt.setAttribute('selected', 'selected')
      prompt.selected = true
    })

    this.productInfo.querySelectorAll('form input[name="id"]').forEach((input) => {
      input.value = ''
    })

    const submitButton = this.productInfo.querySelector('.product-form__submit')
    if (submitButton) {
      submitButton.disabled = true
      const label = submitButton.querySelector('span')
      if (label) label.textContent = this.productInfo.dataset.chooseOptions || window.variantStrings?.unavailable || 'Choose options'
    }

    this.productInfo.querySelector('.shopify-payment-button')?.setAttribute('hidden', '')
  }

  playDeferredVideos() {
    this.productInfo.querySelectorAll('deferred-media[data-autoplay-video]').forEach((media) => {
      if (media.hasAttribute('loaded')) return
      media.querySelector('.deferred-media__poster')?.click()
    })
  }

  destroy() {
    this.observer?.disconnect()
    if (this.instructionId) {
      this.productInfo.querySelectorAll(`[aria-describedby="${this.instructionId}"]`).forEach((control) => {
        control.removeAttribute('aria-describedby')
      })
    }
    this.optionChangeUnsubscriber?.()
    this.variantChangeUnsubscriber?.()
  }
}

const featuredProductInstances = new WeakMap()

function initializeFeaturedProducts(root = document) {
  const productInfos = root.matches?.('product-info[data-featured-product]')
    ? [root]
    : root.querySelectorAll('product-info[data-featured-product]')

  productInfos.forEach((productInfo) => {
    if (featuredProductInstances.has(productInfo)) return
    featuredProductInstances.set(productInfo, new FeaturedProductMediaAutoplay(productInfo))
  })
}

function destroyFeaturedProducts(root) {
  const productInfos = root.matches?.('product-info[data-featured-product]')
    ? [root]
    : root.querySelectorAll('product-info[data-featured-product]')

  productInfos.forEach((productInfo) => {
    featuredProductInstances.get(productInfo)?.destroy()
    featuredProductInstances.delete(productInfo)
  })
}

window.featuredProductController = {
  initialize: initializeFeaturedProducts,
  destroy: destroyFeaturedProducts,
}

initializeFeaturedProducts()
document.addEventListener('shopify:section:load', (event) => initializeFeaturedProducts(event.target))
document.addEventListener('shopify:section:unload', (event) => destroyFeaturedProducts(event.target))
})()
